import { type ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

export function Modal({ open, onClose, children, className }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) {
      el.showModal();
      queueMicrotask(() => {
        if (el.contains(document.activeElement)) return;
        const focusable = el.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([aria-label="Close"]), [tabindex]:not([tabindex="-1"])',
        );
        focusable?.focus();
      });
    } else {
      el.close();
    }
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto overflow-visible rounded-[var(--radius-card)] border-none bg-white p-0 shadow-lg backdrop:bg-black/40"
      onClose={onClose}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={cn('flex max-h-[calc(100vh-2rem)] w-[440px] flex-col overflow-hidden', className)}>
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 cursor-pointer text-text-secondary hover:text-text-primary"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        {children}
      </div>
    </dialog>
  );
}

export function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto p-6', className)}>{children}</div>
  );
}

export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end gap-2 border-t border-outlines bg-white px-6 py-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
